/**
 * Lydsignaler for startprosedyren.
 *
 * Web Audio API brukes for å generere presise "tut"-lyder. Ingen lydfiler
 * å laste ned. Mobile browsere krever at AudioContext resumes() etter
 * brukerinteraksjon — kall `unlockAudio()` fra en click/touch-handler.
 */

let ctx: AudioContext | null = null;
let unlocked = false;

export function isAudioUnlocked(): boolean {
  return unlocked && ctx?.state === 'running';
}

/**
 * Må kalles i respons til bruker-interaksjon (knapp-trykk).
 */
export async function unlockAudio(): Promise<boolean> {
  try {
    if (!ctx) {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) {
        console.warn('Web Audio API ikke tilgjengelig');
        return false;
      }
      ctx = new AC();
    }
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    // Spill en stille tick for å "varme opp" lyden på iOS
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = 0;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.01);

    unlocked = true;
    return true;
  } catch (err) {
    console.error('Klarte ikke låse opp lyd:', err);
    return false;
  }
}

/**
 * Spill en kort "tut" (ca 0,25 sek, 800 Hz firkantbølge).
 */
export function playShortBeep() {
  playBeep({ frequency: 800, durationMs: 250, volume: 0.6 });
}

/**
 * Spill et langt signal (ca 0,9 sek). Brukes ved −1 min og start.
 */
export function playLongBeep() {
  playBeep({ frequency: 700, durationMs: 900, volume: 0.7 });
}

/**
 * To korte lyder (f.eks. ved utsettelse — AP).
 */
export function playDoubleBeep() {
  playBeep({ frequency: 650, durationMs: 220, volume: 0.6 });
  setTimeout(() => playBeep({ frequency: 650, durationMs: 220, volume: 0.6 }), 350);
}

interface BeepOptions {
  frequency: number;
  durationMs: number;
  volume: number;
}

function playBeep({ frequency, durationMs, volume }: BeepOptions) {
  if (!ctx || !unlocked) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = 'square';
  osc.frequency.value = frequency;

  // Enkel ADSR for å unngå klikk
  const dur = durationMs / 1000;
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(volume, now + 0.01);
  gain.gain.setValueAtTime(volume, now + dur - 0.05);
  gain.gain.linearRampToValueAtTime(0, now + dur);

  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + dur + 0.05);
}
